// LANDING v3 — **HER AD TARGET, AND THE ADS ARE RUNNING.**
//
// >>> HER ART IS THE PAGE. THE MOCKUP-MATCHING LAW GOVERNS: NOTHING HERE IS REBUILT AS TYPE. <<<
// The hero, her three copy lines, the four question cards, the three panels, the reviews she painted,
// the healing line and every CTA plate are all painted INTO her composition. What the app adds is a
// set of TRANSPARENT TARGETS laid over the artwork, plus the two live controls she asked for by name:
// the Ask Lala text input inside her painted box, and a LOG IN button top-right.
//
// ════════════════════════════════════════════════════════════════════════════════════════════════
// >>> EVERY COORDINATE BELOW WAS SCANNED OUT OF HER OWN PIXELS (R25/R180), NEVER PLACED BY EYE. <<<
//
// `scripts/landing-scan.mjs` finds the cream bands, the panel columns, the bright input box and the
// plum CTA plates in each master and prints the rows and columns they really occupy. A zone placed by
// eye is the DEAD TAP her own clause exists to prevent — and on the one page her advertising pays for,
// a dead tap is money burned with no symptom at all (R8-4's silent direction).
//
// **THE TWO FILES ARE DIFFERENT LAYOUTS, NOT ONE SCALED**, which is why there are two maps. Her
// desktop master is 1817x866 (aspect 2.098 — her *wider/thinner proportion*) and lays the four cards
// in a row with three panels beneath; her vertical is 941x1672 and stacks them. One map stretched
// across both would put her phone's zones over her desktop's artwork, silently, at one range of
// widths (HOME-1's own ruling, and ROOM ART paid for it in the other direction).
// ════════════════════════════════════════════════════════════════════════════════════════════════
//
// **IT IMPORTS NOTHING**, which is the entry requirement that lets a `.mjs` gate LOAD and RUN every
// law here rather than reading a regex out of a TSX file (R146: reach is transitive).

/** The DOOR a zone opens. `signup` and `login` are DOORS rather than router pages (HOME-1's ruling:
 *  a page named "signup" does not exist and must not be invented to satisfy a check — R141).
 *
 *  >>> `talk` IS THE THIRD, AND IT IS A DOOR TOO RATHER THAN A PAGE NAVIGATION. <<< `/talk` really
 *  IS a router page (F4-1), but WHERE the tap lands is `talkStartPlan`'s decision: a member who is
 *  already signed in goes straight into the conversation and a stranger goes through signup with
 *  the landing stashed. A bare page navigation here would be a second answer to that question
 *  (R28/R29), and the copy that drifted would be the one on the page her advertising pays for. */
export const SIGNUP = "signup";
export const LOGIN = "login";
export const TALK = "talk";

/**
 * **THE KINDS, AND WHY THERE ARE SIX RATHER THAN THREE.**
 *
 * Her routing order names six destinations and a seventh arrived an hour later, and they are NOT
 * interchangeable: three go to a FUNNEL door with a key, two go to SIGNUP for two different reasons,
 * one is a live INPUT that routes on the first keystroke, and one is LOG IN. Folding any pair would
 * be a second answer to where a tap lands on the page her advertising points at (R28/R29).
 */
export const ZONE_KINDS = Object.freeze(["funnel", "signup", "interest", "ask-lala", "login"]);

/**
 * **HER THREE FUNNEL DOORS, BY HER OWN KEYS.**
 *
 * Her order: MONEY -> `/funnel?f=wealthy`, PSYCHIC GIFTS -> `/funnel?f=psychic`, TIMING ->
 * `/funnel?f=timeline`. The key is a CONTRACT WITH LIVE ADVERTISING (F1's own ruling) and is never
 * re-spelled here — it is checked against `FUNNEL_KEYS` by the fixture, so a zone naming a door this
 * house does not have is RED rather than a paid click landing on the fallback.
 */

/**
 * **AND RELATIONSHIPS IS NOT ONE OF THEM, WHICH IS HER OWN CLAUSE (f).**
 *
 * *"RELATIONSHIP → signup (no relationship funnel exists yet, tagged as relationship-interest in the
 * lead row for the day one does)."* A fourth funnel key would be a paid click landing on a door that
 * does not exist; a plain signup loses the fact that she asked about love. So it is its own kind, it
 * lands on SIGNUP, and it CARRIES A TAG — which is the difference between *we do not have that yet*
 * and *we did not notice you asked*.
 */
export const INTEREST_TAG = "relationship-interest";

// ---------------------------------------------------------------------------------------------
// >>> THE DESKTOP MAP — 1817x866, every number a percentage of HER ARTWORK's own box. <<<
//
// Scanned: the four-card cream band is y 335..441 (38.68%..50.92%) running x 112..1707; its three
// dotted dividers sit at x 573, 934 and 1330. The three-panel band is y 451..672 (52.08%..77.60%)
// with panel edges at x 112..619, 628..1140 and 1149..1701. Her Ask Lala input box is the bright
// white run x 655..1115, y 507..612. The hero plate is x 693..1107, y 243..302, bounded by her own
// gold rules top and bottom.
// ---------------------------------------------------------------------------------------------
export const DESKTOP_ZONES = Object.freeze([
  // Her clause (e): the primary CTA, painted twice more below and once at the foot. Every one of them
  // is the SAME act, so every one carries the same kind — a stranger who taps the third copy of a
  // button must not land somewhere different from one who taps the first.
  { id: "d-hero-chart", kind: "signup", label: "Get your free chart now",
    left: 38.14, top: 28.06, width: 22.78, height: 6.81 },

  // Her four question cards, left to right, on the dividers she painted.
  { id: "d-relationships", kind: "interest", label: "Relationships",
    left: 6.16, top: 38.68, width: 25.38, height: 12.24 },
  { id: "d-money", kind: "funnel", funnel: "wealthy", label: "Money",
    left: 31.54, top: 38.68, width: 19.86, height: 12.24 },
  { id: "d-psychic", kind: "funnel", funnel: "psychic", label: "Psychic gifts",
    left: 51.40, top: 38.68, width: 21.80, height: 12.24 },
  { id: "d-timing", kind: "funnel", funnel: "timeline", label: "Timing",
    left: 73.20, top: 38.68, width: 20.75, height: 12.24 },

  // >>> HER CLAUSE (d): THE ASK LALA BOX IS A REAL INPUT AND NOT A ZONE. <<< It is declared here so
  // the fixture holds its geometry and its tap floor with every other door, and the page renders a
  // real `<input>` at exactly this box rather than a transparent button — because her spec is that a
  // visitor CLICKS IN AND TYPES, and a transparent button cannot be typed into.
  { id: "d-ask-lala", kind: "ask-lala", label: "Ask Lala", typed: true,
    left: 36.05, top: 58.55, width: 25.31, height: 12.12 },

  // The three panel CTAs she painted, all the same act as the hero.
  { id: "d-analysis-chart", kind: "signup", label: "Get your free chart analysis",
    left: 11.01, top: 73.33, width: 19.54, height: 3.93 },
  // >>> HER 2026-08-25 RULING, OPTION B: THIS ONE DOOR GIVES UP HI1's FLOOR ON A MOUSE. <<<
  // It sits 0.92% of the artwork below her Ask Lala box, and its own declared height is 5.20% — so
  // as the artwork shrinks the ABSOLUTE 44px floor grows it UPWARD into the top of that box and a
  // click there focuses nothing and navigates to signup. A WRONG door rather than a dead one, and
  // measured at 221x5px at 1280, 239x3 at 1366, 255x2 at 1440 and NOTHING at 1920.
  //
  // **THE FLOOR IS A TAP FLOOR AND A MOUSE IS PIXEL-PRECISE**, so it is scoped off on `pointer: fine`
  // ALONE — her Option B, which moves NO painted element and NO centre and leaves every other zone's
  // floor exactly where it is (Option A took five zones to 19px and was refused with its numbers).
  // **THE RESIDUAL IS DECLARED RATHER THAN HIDDEN (R17/R160): on a device that can be TOUCHED the
  // floor stays and the overlap returns there**, which is her accepted trade and is in DECISIONS
  // ITEM1-OVERLAP-2. The exemption is a PROPERTY here rather than an id typed into the stylesheet,
  // so there is ONE answer to which door it is (R28/R29) and a rename goes RED rather than pointing
  // the sheet at nothing.
  { id: "d-lala-chart", kind: "signup", label: "Get your free chart now", mouseExact: true,
    left: 37.97, top: 71.59, width: 21.47, height: 5.20 },
  { id: "d-reviews-chart", kind: "signup", label: "Get your free chart now",
    left: 68.52, top: 73.33, width: 18.44, height: 3.93 },

  // Her foot banner.
  { id: "d-foot-chart", kind: "signup", label: "Get your free chart now",
    left: 34.67, top: 90.30, width: 30.27, height: 7.50 },
]);

// ---------------------------------------------------------------------------------------------
// >>> THE PHONE MAP — 941x1672, scanned the same way. <<<
//
// Her four-card band is y 430..718 (25.72%..42.94%) with the four text columns at x 35..252,
// 255..454, 458..658 and 661..891. Her Ask Lala input is x 177..846, y 1015..1156. The hero plate is
// x 260..685, y 294..375, and her "Ask Lala Now" plate is y 1168..1234.
//
// **HER VERTICAL COMPOSITION IS NOT THE DESKTOP ONE RESTACKED**: it paints "WHY 12TH HOUSE IS
// DIFFERENT" as a DARK band with no CTA in it, and its Ask Lala panel closes on *"Ask Lala Now"*
// rather than a chart CTA — so this map carries one fewer signup plate, and that is a fact about her
// art rather than an omission.
// ---------------------------------------------------------------------------------------------
export const PHONE_ZONES = Object.freeze([
  { id: "p-hero-chart", kind: "signup", label: "Get your free chart now",
    left: 27.63, top: 17.58, width: 45.16, height: 4.85 },

  { id: "p-relationships", kind: "interest", label: "Relationships",
    left: 3.72, top: 25.72, width: 23.18, height: 17.22 },
  { id: "p-money", kind: "funnel", funnel: "wealthy", label: "Money",
    left: 26.90, top: 25.72, width: 21.50, height: 17.22 },
  { id: "p-psychic", kind: "funnel", funnel: "psychic", label: "Psychic gifts",
    left: 48.40, top: 25.72, width: 21.70, height: 17.22 },
  { id: "p-timing", kind: "funnel", funnel: "timeline", label: "Timing",
    left: 70.10, top: 25.72, width: 24.59, height: 17.22 },

  { id: "p-ask-lala", kind: "ask-lala", label: "Ask Lala", typed: true,
    left: 18.81, top: 60.71, width: 71.09, height: 8.43 },

  // >>> HER PAINTED "ASK LALA NOW" PLATE, AND THE PRODUCTION WALK IS WHAT FOUND IT ANSWERING
  // NOTHING. <<< It is the same ACT as the box above it (both land on `/talk`) and it is NOT the
  // typable one -- two text fields asking one question on one phone screen is the reinterpretation
  // the mockup-matching law refuses. So it carries no `typed` flag and renders as an ordinary
  // transparent door.
  { id: "p-ask-now", kind: "ask-lala", label: "Ask Lala now",
    left: 21.57, top: 69.86, width: 73.97, height: 3.82 },
]);

/** >>> WHERE A ZONE LANDS. One law, read by the page and by the fixture alike (R28/R29). <<< */
export function zoneDestination(zone) {
  if (!zone || typeof zone !== "object") return null;
  if (zone.kind === "funnel") {
    // The KEY is the whole destination for a funnel door, so a zone with none lands NOWHERE rather
    // than on the fallback — a paid click filed under the wrong ad is worse than one that fails
    // loudly (F1's own three-facts ruling about `f`).
    return typeof zone.funnel === "string" && zone.funnel ? `funnel:${zone.funnel}` : null;
  }
  if (zone.kind === "signup" || zone.kind === "interest") return SIGNUP;
  // >>> HER 2026-08-23 ORDER SUPERSEDES CLAUSE (d)'s OWN WORDING, AND THE REASON IS THAT THE DOOR
  // IT WAS WAITING FOR NOW EXISTS. <<< That clause read *until FUNNEL-4 ships, route to SIGNUP with
  // the lala-funnel continuity token so arrivals are tagged* -- a placeholder for a door that had
  // not been built. **F4-1 built it** (`/talk`, `src/talkDoor.js`), and her order names it: *Ask
  // Lala box to /talk*. So the tag it stood in for is RETIRED AT ITS DECLARATION rather than left
  // standing beside its replacement (DL-2/DT-2b/R181), and the box goes to the conversation door.
  if (zone.kind === "ask-lala") return TALK;
  if (zone.kind === "login") return LOGIN;
  return null;
}

/**
 * **WHAT RIDES WITH HER, AND IT IS NOT THE SAME THING AS WHERE SHE LANDS (R18).**
 *
 * Two zones land on SIGNUP and mean two different things: an ordinary CTA, and *she asked about
 * love*. Collapsing them loses the one fact the second was for -- her clause (f) is about exactly
 * that: a tag kept for the day the door exists.
 *
 * >>> AND `lala-funnel` IS GONE, WHICH IS A RETIREMENT RATHER THAN AN OMISSION (R17/R160). <<<
 * It existed because the Ask Lala box had nowhere to land; it lands on `/talk` now, so a tag
 * standing in for a missing door would be a value written to a member's row that nothing was ever
 * going to read (R38/R165's trap). ONE tag, and the roster below is what makes it safe to take one
 * off a request body at all.
 */
export function zoneTag(zone) {
  if (!zone || typeof zone !== "object") return "";
  if (zone.kind === "interest") return INTEREST_TAG;
  return "";
}

/** What a screen reader calls a transparent button over a painting — the ONLY name it has. */
export function zoneLabel(zone = {}) {
  const label = typeof zone.label === "string" ? zone.label.trim() : "";
  return label || String(zone.id || "");
}

/** Every zone on a map, refused if it would fall outside her artwork or land nowhere. */
export function zoneViolations(zone) {
  const out = [];
  if (!zone?.id) out.push("no-id");
  if (!ZONE_KINDS.includes(zone?.kind)) out.push("unrostered-kind");
  for (const k of ["top", "left", "width", "height"]) {
    const v = zone?.[k];
    // R53/R136/R137 — a real number, never a coercion: `Number(true)` is 1 and would place a zone
    // one percent from the edge of the page her advertising pays for.
    if (typeof v !== "number" || !Number.isFinite(v)) { out.push(`${k}-not-a-number`); continue; }
    if (v < 0) out.push(`${k}-negative`);
  }
  if (Number.isFinite(zone?.left) && Number.isFinite(zone?.width) && zone.left + zone.width > 100.5) out.push("overruns-right");
  if (Number.isFinite(zone?.top) && Number.isFinite(zone?.height) && zone.top + zone.height > 100.5) out.push("overruns-bottom");
  if (!zoneDestination(zone)) out.push("no-destination");
  // >>> ONLY AN ASK LALA DOOR MAY BE HER TEXT FIELD, AND ONLY A REAL `true` DECLARES ONE. <<< A
  // truthy value here is a second `<input>` over her painted art; the flag on any other kind is a
  // text box where she painted a button.
  if (Object(zone).typed !== undefined && zone.typed !== true) out.push("typed-not-true");
  if (zone?.typed === true && zone?.kind !== "ask-lala") out.push("typed-not-an-ask-lala-door");
  // >>> AND THE MOUSE-EXACT EXEMPTION IS PRESENCE RATHER THAN TRUTHINESS FOR THE SAME REASON. <<<
  // `mouseExact: 1` is truthy, and a zone that quietly gave up HI1's floor is a target a member can
  // no longer hit on the page her advertising pays for — the silent direction (R8-4). Only a real
  // `true` gives it up, and only on a pointer that is pixel-precise.
  if (Object(zone).mouseExact !== undefined && zone.mouseExact !== true) out.push("mouse-exact-not-true");
  return out;
}

/**
 * >>> WHICH DOOR IS HER TEXT FIELD, DECLARED RATHER THAN INFERRED FROM ORDER (R41). <<<
 *
 * Her phone artwork paints TWO Ask Lala elements: the box a stranger types into, and the "Ask Lala
 * Now" plate under it. Both are the same ACT, both are `ask-lala` doors, and only one of them can be
 * an `<input>`.
 *
 * **THE FIRST BUILD PICKED IT BY POSITION AND HER PRODUCTION WALK MEASURED WHAT THAT COST**: the page
 * rendered an input for the FIRST `ask-lala` zone and filtered EVERY `ask-lala` zone out of the
 * buttons, so **her painted plate rendered nothing at all** -- a rostered door answering nothing, on
 * the viewport her advertising really lands on, with no symptom whatever (R8-4, and R38/R165's trap:
 * a promise the house makes in its own artwork and does not keep). A POSITION IS NOT A PROPERTY: the
 * day her map lists the plate first, the plate becomes the field and the box becomes a button,
 * silently. So it is a flag on the zone she paints as a field, and the fixture holds that each
 * viewport declares exactly one.
 *
 * **PRESENCE, NEVER COERCION (R53/R136/R137):** only a real `true` is her field, because `typed: 1`
 * is truthy and would turn her plate into a second text box on the page her ad spend lands on.
 */
/**
 * >>> WHICH DOORS GIVE UP HI1's TAP FLOOR ON A MOUSE — HER 2026-08-25 OPTION B. <<<
 *
 * HI1's 44px floor is a **TAP** floor: it exists because a thumb is imprecise, and it grows a zone
 * around her painted element rather than moving it. On a **pixel-precise pointer** that growth buys
 * nothing and can cost something — as her artwork shrinks it pushed `d-lala-chart` UP into the top
 * of her Ask Lala box, so a click in that band focused nothing and navigated to signup.
 *
 * **IT IS A ROSTER READ ONCE RATHER THAN AN ID TYPED INTO THE STYLESHEET (R28/R29).** The sheet names
 * a PROPERTY, the map names the DOOR, and the component carries one to the other — so there is a
 * single answer to which zone this is, and a rename cannot leave a rule pointing at nothing while
 * the overlap comes back with no symptom.
 *
 * **WHAT IT DELIBERATELY DOES NOT DO (R17/R160):** it does not answer for a touch device, where the
 * floor stays and the overlap returns. That residual is her accepted trade, it is recorded in
 * DECISIONS ITEM1-OVERLAP-2, and the honest instrument for it is a real thumb.
 */
/**
 * >>> THE FLOOR A MOUSE-EXACT DOOR IS STILL HELD TO. <<<
 *
 * Her Option B gives back HI1's 44px THUMB floor on a pixel-precise pointer — it does not make a door
 * floorless. **The whole reason B beat A is a number**: B leaves its one door at 26px / 28px / 29px at
 * her three widths, all above published mouse-target guidance, where A took five doors to 19px. So
 * that guidance is declared HERE, beside the exemption it justifies, and the walk is held to it —
 * otherwise "exempt" quietly means "unmeasured" and a door that collapsed to nothing would pass.
 */
export const MOUSE_TAP_PX = 24;

export function mouseExactZones(zones) {
  const list = Array.isArray(zones) ? zones : [];
  return list.filter((z) => z?.mouseExact === true).map((z) => z.id);
}

export function typedZone(zones) {
  const list = Array.isArray(zones) ? zones : [];
  return list.find((z) => z?.typed === true && z?.kind === "ask-lala") || null;
}

/** Two zones may not overlap: a stacked pair means one of her elements is untappable. */
export function overlappingZones(zones) {
  const hits = [];
  const list = Array.isArray(zones) ? zones : [];
  for (let i = 0; i < list.length; i += 1) {
    for (let j = i + 1; j < list.length; j += 1) {
      const a = list[i];
      const b = list[j];
      const ox = Math.min(a.left + a.width, b.left + b.width) - Math.max(a.left, b.left);
      const oy = Math.min(a.top + a.height, b.top + b.height) - Math.max(a.top, b.top);
      if (ox > 0.4 && oy > 0.4) hits.push(`${a.id}/${b.id}`);
    }
  }
  return hits;
}

// **ONE BREAKPOINT, READ BY THE `<picture>` MEDIA QUERY AND BY THE ZONE MAP ALIKE.** Two numbers
// would offer one layout and map the other's zones onto it — a whole page of dead taps at exactly
// one range of widths, on the page her ad spend lands on.
export const LANDING_ART_BREAKPOINT = 820;

/**
 * **HER ART'S OWN CRISP WIDTH, MEASURED RATHER THAN CARRIED OVER.**
 *
 * Her desktop master is 1817px wide. HOME-1's `--art-crisp` is 1672 and is a fact about the art it
 * was measured on (W1-11/LP2/R65's shape: a number stated beside a law is a receipt). Past this the
 * wrapper caps so her artwork is never upscaled past its own resolution, and because every zone is an
 * `inset: 0` percentage of that wrapper, the artwork and its whole tap map scale together — so not
 * one coordinate is re-measured at any width.
 */
export const LANDING_CRISP_PX = 1817;

export const MIN_TAP_PX = 44;

/** The centre of a zone, which is where a hit test asks and where a thumb really lands. */
export function zoneCenter(zone) {
  if (!zone || typeof zone.left !== "number") return null;
  return { x: zone.left + zone.width / 2, y: zone.top + zone.height / 2 };
}

/**
 * >>> HER LOG IN BUTTON (her 2026-08-22 addition). <<<
 *
 * *"A LOG IN button at the TOP RIGHT corner of the landing, both desktop and mobile: gold,
 * house-styled, small and elegant (never competing with the conversion doors)."*
 *
 * **IT IS A RENDERED CONTROL RATHER THAN A ZONE OVER PAINTED ART, AND THAT IS FORCED RATHER THAN
 * CHOSEN.** Neither master paints a LOG IN — the top-right of each carries her astrology wheel and
 * her moon — so a transparent target there would be a button over nothing, which is the DEAD TAP her
 * own clause forbids. The page draws a real one.
 *
 * **NEVER COMPETING IS ARITHMETIC RATHER THAN TASTE:** it is held SMALLER than every conversion door
 * on its own map, so a later restyle cannot quietly make the way back in louder than the way in.
 */
export const LOGIN_ZONE = Object.freeze({
  id: "login", kind: "login", label: "Log in",
  left: 88.6, top: 1.6, width: 9.4, height: 5.0,
});

export const LOGIN_ZONE_PHONE = Object.freeze({
  id: "login", kind: "login", label: "Log in",
  left: 72.0, top: 1.2, width: 24.0, height: 3.4,
});

/** The map a viewport really gets, LOG IN included — one answer, so the page and the fixture cannot
 *  disagree about how many doors a stranger is offered (R28/R29). */
export function zonesFor(desktop) {
  return desktop
    ? [...DESKTOP_ZONES, LOGIN_ZONE]
    : [...PHONE_ZONES, LOGIN_ZONE_PHONE];
}

/**
 * **IS THIS DOOR SMALLER THAN EVERY CONVERSION DOOR ON THE SAME MAP?**
 *
 * Her clause is *never competing with the conversion doors*, and the honest reading of that is a
 * comparison rather than a size: what matters is that LOG IN is the quietest thing on the page, at
 * whatever width. Held as a PAIR (R43's M8) — there really ARE conversion doors to be quieter than,
 * or the clause passes over a page with nothing on it.
 */
export function loginIsQuietest(zones) {
  const list = Array.isArray(zones) ? zones : [];
  const login = list.find((z) => z && z.kind === "login");
  const doors = list.filter((z) => z && z.kind !== "login");
  if (!login || !doors.length) return false;
  const area = (z) => z.width * z.height;
  return doors.every((d) => area(d) > area(login));
}

// =============================================================================
// >>> HER CLAUSES (d) AND (f): WHICH LANDING DOOR BROUGHT HER, KEPT. <<<
// =============================================================================
//
// *"(f) RELATIONSHIP -> signup, tagged relationship-interest in the lead row for the day a
// relationship funnel exists."* and *"(d) ... route to SIGNUP with the lala-funnel continuity token
// so arrivals are tagged."*
//
// **THIS IS THE SAME QUESTION `origin_reading` ANSWERS AND IT IS DELIBERATELY NOT THAT COLUMN
// (R18).** That one is *which READING she clicked to arrive*, and its own law refuses anything that
// is not a reading this house delivers — so `relationship-interest` handed to it writes NOTHING and
// records NOTHING, which reads as wired and is not (R38/R165's trap). *Which reading* and *which
// landing door* are two facts and they are two columns.
//
// **THE ROSTER IS CLOSED AND AN UNROSTERED TAG RESOLVES TO NOTHING**, which is `resolveOrigin`'s own
// direction one column over: what travels here is a CLAIM off a request body, so the roster is what
// makes it safe to take one at all. A door added next year joins by being DECLARED.
// >>> HER `/talk` DOOR JOINS THE ROSTER, 2026-08-26, AND IT IS WHY TONIGHT WAS UNMEASURABLE. <<<
//
// Her funnel email went to 1,700 people and every one of them lands on `/talk`. That door mints
// the account ITSELF (ITEM 2's capture) rather than going through signup, so it never carried a
// tag -- and the roster below would have refused one anyway, because a door joins by being
// DECLARED and this one never was. Measured on her live rows: 52 accounts today, 0 tagged, and
// exactly ONE tagged member all time, from 25 August. The attribution was dark.
//
// It is the DOOR, not the funnel key: `/talk` is deliberately not on `FUNNEL_KEYS` (talkDoor.js
// rules on that), and this roster answers a different question -- which landing brought her.
export const TALK_TAG = "ask-lala";

// >>> **HER THREE AD DOORS JOIN THE ROSTER, 2026-08-27, AND THE MEASUREMENT IS THE REASON.** <<<
//
// 208 signups in five days and NINE of them carried a tag. The two doors that were declared are the
// only two that ever wrote one, and the doors her advertising actually pays for -- `/funnel?f=` --
// were dark: 364 members are named by a converted lead row whose `funnel` column has said which
// door caught them the whole time, and not one of them carried the tag that answers the question.
//
// **THEY ARE THE FUNNEL KEYS, SPELLED ONCE.** `FUNNEL_KEYS` is the roster in `src/funnels.js` and a
// second spelling here is a door that stops being attributable the day she renames one (R28/R29/R31).
// This module imports NOTHING by law, so the values are DECLARED here and a fixture holds the two
// lists to each other rather than a runtime import doing it.
export const FUNNEL_DOOR_TAGS = Object.freeze(["wealthy", "psychic", "timeline"]);

export const LANDING_TAGS = Object.freeze([INTEREST_TAG, TALK_TAG, ...FUNNEL_DOOR_TAGS]);

export function resolveLandingTag(tag) {
  const t = typeof tag === "string" ? tag.trim() : "";
  return LANDING_TAGS.includes(t) ? t : "";
}

// >>> THE STASH, AND WHY IT IS `sessionStorage` (L10's own recorded reason, one door over). <<<
//
// Her tag is decided on the landing and is needed at ROOM-CREATE, which is a whole navigation and,
// on the Google path, a full round trip out of this origin and back. State does not survive that.
// `sessionStorage` does, and it starts clean in a new session -- where a `localStorage` stash would
// pour one stranger's arrival over whoever uses that browser next week.
//
// >>> WHAT SHE TYPED IS NOT CARRIED, AND THAT IS A RETIREMENT WITH ITS REASON RATHER THAN A GAP
// (R17/R160). <<< Her clause (d) asked for it while the Ask Lala box had nowhere to land; the box
// lands on `/talk` now. And the box routes on the FIRST FOCUS OR KEYSTROKE, which is her own
// clause -- so what a carry would hold is at most one character, and the reader for it does not
// exist: `/talk`'s capture asks her name, her address and her birth details and never a first
// question. A stash nothing reads is the same defect one layer up from a column nothing reads
// (R38/R165), so there is one fact here and it is the DOOR she came through.
const LANDING_STASH_KEY = "twelvehouse.landing";

// >>> HER RULING 2026-08-26 REVERSES THE RETIREMENT ABOVE: WHAT SHE TYPED RIDES WITH HER. <<<
//
// The paragraph above retired the carry for a measured reason -- the box routed on the FIRST
// focus or keystroke, so there was at most one character to carry and nothing at the other end
// read it. Her ruling changes BOTH halves: the box lets her finish her sentence now, and
// `AskLalaPage`'s `pendingQuestion` pipe is the reader, which already SUBMITS what it is handed.
// So her question is not pre-filled for her to send again -- it is the first thing she asks.
//
// `sessionStorage` for `onboarding.ts`'s own recorded reason: a new session starts clean, so one
// stranger's question cannot be poured over whoever uses that browser next week. Read and
// CLEARED in one act, because a question left standing would be asked again on a later arrival.
const ASK_TEXT_KEY = "twelvehouse.askText";

// A question is her sentence, not a stray keystroke: under two characters there is nothing to
// carry, and the ceiling is the composer's own so nothing can be stashed that cannot be sent.
export const ASK_TEXT_MIN = 2;
export const ASK_TEXT_MAX = 2000;

export function writeAskText(storage, text) {
  const clean = String(text || "").trim().slice(0, ASK_TEXT_MAX);
  try {
    if (clean.length < ASK_TEXT_MIN) { storage?.removeItem?.(ASK_TEXT_KEY); return ""; }
    storage?.setItem?.(ASK_TEXT_KEY, clean);
  } catch { /* a storage that refuses is a browser setting, never a lost tap */ }
  return clean;
}

export function readAskText(storage) {
  try {
    const v = storage?.getItem?.(ASK_TEXT_KEY) ?? "";
    storage?.removeItem?.(ASK_TEXT_KEY);
    return typeof v === "string" ? v : "";
  } catch {
    return "";
  }
}

// >>> **THE SECOND CARRIER, AND THE WALK THAT FORCED IT (TIKTOK-WEBVIEW-1, 2026-09-04).** <<<
//
// The paragraph below used to end *"a storage that throws is a browser setting rather than a defect,
// and it may never cost her the signup she is in the middle of."* **It cost her the signup.** Walked
// on production in a storage-partitioned TikTok webview, the whole chain ran — the page painted, the
// form took her details, the chart cast in 82 seconds, Lala greeted her BY NAME — and then:
//
//     NO   >>> THE PLUM WINDOW IS ON SCREEN     — the page is there; dressed=false
//     NO   THE METER is visible and reads FIVE  — no meter element
//     NO   0 of 5 turns went through
//     ...and she was handed "Pick the one that fits where you are right now. Choose a membership".
//
// **THE WRITE SWALLOWED ITS THROW AND THE READ CAME BACK EMPTY, SO THE HOUSE FORGOT SHE CAME THROUGH
// THE FUNNEL ONE LINE AFTER MINTING HER SESSION.** `App`'s `funnelArrival` went false, `talk-close`'s
// documented deadlock closed — *"the door she is refused at is the door that grants her allotment"* —
// and a woman who did everything right was shown a paywall instead of her five free turns.
//
// **SO THE TAG GETS A CARRIER THAT CANNOT BE TAKEN AWAY: THIS MODULE.** The write and the read
// happen in ONE page life — `TalkCapture` writes it, then `goTo("ask-lala")` renders the reader with
// no reload in between — so a module-scope value carries it across exactly the gap that matters.
//
// **AND IT DOES NOT WEAKEN R8-4's CLAUSE, WHICH IS THE REASON THIS LAW IS CAREFUL.** A stale tag
// filing an organic signup under an advertisement is prevented by the SAME two acts as before: an
// empty tag CLEARS both carriers, and `clearLandingArrival` clears both. The memory carrier is
// strictly shorter-lived than the storage one — a reload empties it — so it can never outlive the
// stash it stands in for. **Storage is still read FIRST**, so nothing here changes what a browser
// with storage does.
let memoryArrival = "";

export function writeLandingArrival(storage, { tag = "" } = {}) {
  const clean = resolveLandingTag(tag);
  // The memory carrier is set OUTSIDE the try, because the whole point is that it survives a storage
  // that raises on the very next line.
  memoryArrival = clean;
  try {
    // A trigger that carries nothing CLEARS rather than leaving the last one standing -- most of
    // the house's signup doors are not landing zones and carry no tag, and a stale one surviving
    // any of them files an organic signup under an advertisement (R8-4's silent direction).
    if (!clean) { storage?.removeItem?.(LANDING_STASH_KEY); return { tag: "" }; }
    storage?.setItem?.(LANDING_STASH_KEY, JSON.stringify({ tag: clean }));
  } catch {
    // A storage that throws is a browser setting rather than a defect. It no longer costs her the
    // signup she is in the middle of, because the line above this `try` already carried the tag.
  }
  return { tag: clean };
}

export function readLandingArrival(storage) {
  try {
    const raw = storage?.getItem?.(LANDING_STASH_KEY);
    // >>> THE STASH IS AUTHORITATIVE WHERE IT EXISTS, and memory answers only where it does not. <<<
    // A reload is the case that matters: storage survives it and memory does not, so preferring
    // memory would answer a stale tag to a browser that holds the true one.
    if (!raw) return { tag: resolveLandingTag(memoryArrival) };
    const parsed = JSON.parse(raw);
    // Presence, never coercion (R53/R136/R137): this comes back off storage, where anything at all
    // can have been written, and a coerced tag is an arrival filed under a door nobody tapped.
    return { tag: resolveLandingTag(typeof parsed?.tag === "string" ? parsed.tag : "") };
  } catch {
    return { tag: resolveLandingTag(memoryArrival) };
  }
}

export function clearLandingArrival(storage) {
  memoryArrival = "";
  try { storage?.removeItem?.(LANDING_STASH_KEY); } catch { /* see above */ }
}
